import * as React from 'react'
import { useCallback } from 'react'
import { Field, useFormikContext } from 'formik'
import { getStyles } from './useStyles'
import { MuiTextFieldFormik } from '../Formik'
import { IFilterSet, IOrderBy } from './typings'
import { IFilterMode } from './typings/IFilterMode'
import { Divider } from './components'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import MenuItem from '@mui/material/MenuItem'
import { getFormStyles } from '@sales-center/components'
import { useTheme } from '@mui/material/styles'

interface Props<T> {
  onClose: () => void
  filterComponent: React.ComponentType<IFilterMode>
  defaultValues: T
  clearedValues: T
  orderOptions: IOrderBy[]
  hideOrderBy?: boolean
  hideCreatedBy?: boolean
  reset?: boolean
  onFocus?: () => void
}

export function Filters<U, T extends IFilterSet<U>>(props: Props<T>) {
  const {
    reset,
    onClose,
    onFocus,
    hideOrderBy,
    hideCreatedBy,
    orderOptions,
    defaultValues,
    clearedValues,
    filterComponent: FilterComponent,
  } = props
  const theme = useTheme()
  const { formButton } = getStyles(theme)
  const styles = getFormStyles()
  const {
    submitForm,
    resetForm,
    values: {
      filters: { search },
    },
  } = useFormikContext<T>()

  const resetRetainSearch = useCallback(
    (values: T) => {
      if (search) {
        resetForm({
          values: {
            ...values,
            filters: {
              ...values.filters,
              search,
            },
          },
        })
      } else resetForm({ values })
    },
    [resetForm, search],
  )

  const onClear = useCallback(
    () => resetRetainSearch(clearedValues),
    [clearedValues, resetRetainSearch],
  )

  const onResetToDefault = useCallback(
    () => resetRetainSearch(defaultValues),
    [defaultValues, resetRetainSearch],
  )

  const onFiltersTouched = useCallback(() => onFocus?.(), [onFocus])

  return (
    <>
      <Box
        display="flex"
        justifyContent="space-between"
        padding={1.7}
        height="70px"
        borderBottom={`1px solid ${theme.palette.divider}`}
      >
        <Button
          sx={formButton}
          color="primary"
          variant="text"
          onClick={onClear}
          data-test-id="filtersClearBtn"
        >
          Clear All
        </Button>

        <Button
          sx={formButton}
          color="primary"
          variant="text"
          onClick={onResetToDefault}
          data-test-id="filtersResetBtn"
        >
          Reset Filters to Default
        </Button>
      </Box>
      <Box
        width="100%"
        overflow="hidden auto"
        paddingBottom={2}
        onClick={onFiltersTouched}
        bottom="70px"
        top="144px"
        position="absolute"
      >
        {!hideOrderBy && (
          <>
            <Box padding={1}>
              <Field
                name="orderBy"
                label="Sort By"
                component={MuiTextFieldFormik}
                select
                fullWidth
              >
                {orderOptions.map(({ value, label }, i) => (
                  <MenuItem key={i} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Field>
            </Box>

            <Divider />
          </>
        )}

        <FilterComponent reset={reset} hideCreatedBy={hideCreatedBy} />
      </Box>
      <Box sx={styles.footer}>
        <Button
          sx={formButton}
          color="primary"
          variant="outlined"
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          sx={formButton}
          color="primary"
          variant="contained"
          onClick={submitForm}
        >
          Apply
        </Button>
      </Box>
    </>
  )
}
